import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  WritableSignal,
} from '@angular/core';
import {FormArray, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MatMenuModule} from '@angular/material/menu';
import {Subscription} from 'rxjs';
import {TaskTrigger} from 'src/app/core/model/task';
import {CommonModule} from '@angular/common';
import {OphCheckboxComponent} from 'src/app/shared/design/oph-checkbox/oph-checkbox.component';
import {TasksService} from '../../../services/tasks.service';
import {TaskTriggerSelectionComponent} from '../../trigger-selection/task-trigger-selection.component';
import {ProjectsTaskCheckButtonComponent} from '../check-button/projects-task-check-button.component';
import {ProjectTaskDialogPanels} from '../project-task-dialog-constants';

// Constants for better maintainability
const MAX_TRIGGERS = 3;
const MAX_HOUR_OPTIONS = 12;
const LARGE_HOUR_OPTIONS = [24, 48, 72];
const DEFAULT_EXPIRATION_DISPLAY = '1 hour';
const NEVER_EXPIRATION = 'never';

@Component({
  selector: 'projects-task-triggers',
  standalone: true,
  imports: [
    CommonModule,
    ProjectsTaskCheckButtonComponent,
    TaskTriggerSelectionComponent,
    MatMenuModule,
    OphCheckboxComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './projects-task-triggers.component.html',
  styleUrl: './projects-task-triggers.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectsTaskTriggersComponent implements OnInit, OnDestroy {
  @Input() form!: FormGroup;
  @Input() newTask: boolean = false;
  @Input() isTriggerPanelValid!: WritableSignal<boolean>;
  @Input() validationErrors: Record<string, boolean>;

  @Output() newTrackerClick = new EventEmitter<number>();
  @Output() changePanel = new EventEmitter<{panel: ProjectTaskDialogPanels; dateTimeControlName?: string}>();

  private readonly tasksService = inject(TasksService);
  private readonly subscriptions = new Subscription();

  // UI Options
  readonly triggeredTaskExpirationOptions: string[] = this.generateExpirationOptions();

  // Component state
  triggerCompletionStatus: boolean[] = [];
  triggeredTaskExpirationDisplay: string = DEFAULT_EXPIRATION_DISPLAY;

  ngOnInit(): void {
    this.setupFormSubscriptions();
    if (!this.newTask) {
      this.initializeFormValues();
    }
    // Ensure trigger completion status is updated before validation
    this.validateAndUpdateTriggers(this.triggersFormArray.value);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // Getters for form controls
  get scheduleControl(): FormGroup {
    return this.form.get('schedule') as FormGroup;
  }

  get triggersFormArray(): FormArray {
    return this.form.get('triggers') as FormArray;
  }

  get triggerEndsValueControl(): FormControl {
    return this.form.get('triggerEndsValue') as FormControl;
  }

  get triggerEndsTypeControl(): FormControl {
    return this.form.get('triggerEndsType') as FormControl;
  }

  // Trigger management methods
  onNoTrigger(): void {
    this.triggersFormArray.clear();
    this.scheduleControl.get('scheduleType')?.setValue('doNotRepeat');
  }

  onAddTrigger(): void {
    if (this.scheduleControl.get('scheduleType').value === 'trigger') {
      return;
    }
    if (this.triggersFormArray.value?.length === 0) this.addNewTrigger();
    this.scheduleControl.get('scheduleType')?.setValue('trigger');
  }

  onClearTrigger(triggerIndex: number): void {
    this.triggersFormArray.removeAt(triggerIndex);

    if (this.hasNoTriggers()) {
      this.addNewTrigger();
    }
  }

  // Trigger ends configuration
  onTriggerEndsValueChange(value: string | number, type: 'days' | 'weeks' | 'tasks'): void {
    const numericValue = this.parseToNumber(value);
    this.triggerEndsValueControl.setValue(numericValue);
  }

  onTriggerEndsTypeChange(type: 'forever' | 'days' | 'weeks' | 'tasks'): void {
    if (type !== this.triggerEndsTypeControl.value) {
      this.triggerEndsTypeControl.setValue(type);
    }
  }

  // Expiration configuration
  onTriggeredTaskExpiration(value: string): void {
    this.form.get('triggeredTaskExpiration')?.setValue(value);
  }

  onTriggerTaskCountBlur(): void {
    const taskCountControl = this.form.get('triggerEndsTaskCountValue');
    const currentValue = taskCountControl?.value;

    if (!currentValue || currentValue === '' || currentValue <= 0) {
      taskCountControl?.setValue(1);
    }
  }

  onInputBlur(): void {
    const value = this.form.get('triggerEndsTaskCountValue')?.value;
    if (value < 1) {
      this.form.get('triggerEndsTaskCountValue')?.setValue(1);
    }
    if (!Number.isInteger(value)) {
      this.form.get('triggerEndsTaskCountValue')?.setValue(Math.round(value));
    }
  }

  // Private helper methods
  private setupFormSubscriptions(): void {
    this.subscriptions.add(
      this.triggersFormArray.valueChanges.subscribe(triggers => {
        this.validateAndUpdateTriggers(triggers);
      })
    );

    this.subscriptions.add(
      this.scheduleControl.get('scheduleType')!.valueChanges.subscribe(() => {
        this.validateAndUpdateTriggers(this.triggersFormArray.value);
      })
    );

    this.subscriptions.add(
      this.form.get('triggeredTaskExpiration')!.valueChanges.subscribe(value => {
        this.updateExpirationDisplay(value);
      })
    );
  }

  private initializeFormValues(): void {
    const currentTriggers = this.triggersFormArray.value;
    const currentExpiration = this.form.get('triggeredTaskExpiration')!.value;

    // If form has no triggers, add one using the service method
    if (!currentTriggers || currentTriggers.length === 0) {
      this.triggersFormArray.push(this.tasksService.getTriggerFormGroup());
    }

    this.validateAndUpdateTriggers(this.triggersFormArray.value);
    this.updateExpirationDisplay(currentExpiration);
  }

  private validateAndUpdateTriggers(triggers: TaskTrigger[]): void {
    this.updateTriggerCompletionStatus(triggers);
    this.updateTriggerPanelValidity(triggers);
    this.addTriggerIfNeeded(triggers);
  }

  private updateTriggerCompletionStatus(triggers: TaskTrigger[]): void {
    this.triggerCompletionStatus = triggers.map(trigger => this.tasksService.checkIfTriggerIsComplete(trigger));
  }

  private updateTriggerPanelValidity(triggers: TaskTrigger[]): void {
    const isValid = this.determineTriggersValidity(triggers);
    this.isTriggerPanelValid.set(isValid);
  }

  private determineTriggersValidity(triggers: TaskTrigger[]): boolean {
    if (this.scheduleControl.get('scheduleType').value !== 'trigger') {
      return true;
    }

    if (triggers.length === 0) {
      return true;
    }

    // Check if at least one trigger is complete (same logic as main dialog)
    return this.triggerCompletionStatus.some(complete => complete);
  }

  private addTriggerIfNeeded(triggers: TaskTrigger[]): void {
    if (this.shouldAddNewTrigger(triggers)) {
      this.addNewTrigger();
    }
  }

  private shouldAddNewTrigger(triggers: TaskTrigger[]): boolean {
    if (!this.isWithinTriggerLimit(triggers) || triggers.length === 0) {
      return false;
    }

    const lastTriggerIndex = triggers.length - 1;
    return this.triggerCompletionStatus[lastTriggerIndex];
  }

  private addNewTrigger(): void {
    this.triggersFormArray.push(this.tasksService.getTriggerFormGroup());
  }

  private updateExpirationDisplay(value: string): void {
    this.triggeredTaskExpirationDisplay = value === NEVER_EXPIRATION ? DEFAULT_EXPIRATION_DISPLAY : value;
  }

  private generateExpirationOptions(): string[] {
    const hourlyOptions = this.generateHourlyOptions();
    const largeHourOptions = this.generateLargeHourOptions();

    return [...hourlyOptions, ...largeHourOptions];
  }

  private generateHourlyOptions(): string[] {
    return Array.from({length: MAX_HOUR_OPTIONS}, (_, i) => {
      const hours = i + 1;
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    });
  }

  private generateLargeHourOptions(): string[] {
    return LARGE_HOUR_OPTIONS.map(hours => `${hours} hours`);
  }

  private hasNoTriggers(): boolean {
    return this.triggersFormArray.length === 0;
  }

  private isWithinTriggerLimit(triggers: TaskTrigger[]): boolean {
    return triggers.length <= MAX_TRIGGERS && triggers.length > 0;
  }

  private parseToNumber(value: string | number): number {
    return typeof value === 'string' ? parseInt(value, 10) : value;
  }
}
